/** Pure rules of the sign-in screens (no React): where to land afterwards, email check, banners and messages. */

/** Only same-origin absolute paths are honoured as a post-login target (`//host` would be an open redirect). */
export function safeRedirect(raw: unknown): string {
  return typeof raw === 'string' && raw.startsWith('/') && !raw.startsWith('//') ? raw : '/';
}

/** The post-login target stored in the OIDC `state` that `signinCallback` hands back (none → `/`). */
export function callbackTarget(user: { state?: unknown } | null | undefined | void): string {
  const state = user && typeof user.state === 'object' && user.state ? (user.state as { redirect?: unknown }) : {};
  return safeRedirect(state.redirect);
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isValidEmail(value: string): boolean {
  return EMAIL_PATTERN.test(value.trim());
}

export const EMAIL_INVALID_MESSAGE = 'Enter the email address of your invitation';

const NOTICES: Record<string, string> = {
  expired: 'Your session expired — sign in again to continue.',
  unauthorized: 'The API rejected the session — sign in again.',
};

/** The banner for the `reason` the console set when it dropped the session (none → no banner). */
export function signInNotice(reason: string | undefined): string | null {
  return reason !== undefined && Object.hasOwn(NOTICES, reason) ? NOTICES[reason]! : null;
}

/** "Could not reach the sign-in service at <host> — <detail>" for a failed redirect start. */
export function signInFailureMessage(host: string, e: unknown): string {
  const detail = e instanceof Error && e.message ? ` — ${e.message}` : '';
  return `Could not reach the sign-in service at ${host}${detail}`;
}

export type SignInKind = 'google' | 'email';

/** Arguments of `signIn`: the email path passes the trimmed address as `login_hint`. */
export function signInArgs(redirect: string, kind: SignInKind, email: string): { redirect: string; loginHint?: string } {
  return { redirect, ...(kind === 'email' ? { loginHint: email.trim() } : {}) };
}
